import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import Api from '../_helpers/Api';
import { useAuthDataContext } from './Auth';
import { useSocketContext } from './Socket';

/**
 * NewReservationsProvider
 * 
 * Uses the existing endpoint:
 * GET /api/reservations/not-reviewed
 * 
 * Expected Response:
 * {
 *   "total": 2,
 *   "data": [...]
 * }
 * 
 * The provider extracts the "total" field to display the badge count.
 */

const NewReservationsContext = createContext();

export const useNewReservationsContext = () => {
    return useContext(NewReservationsContext);
};

export function NewReservationsProvider({ children }) {
    const auth = useAuthDataContext();
    const listener = useSocketContext();
    const partnerId = auth ? auth.partnerId() : null;

    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);

    // Callbacks registered by mounted components (e.g. the reservations page)
    // that want to react to live reservation socket events.
    const subscribersRef = useRef(new Set());

    const loadCount = () => {
        if (!auth || auth.isAdmin()) {
            setCount(0);
            setLoading(false);
            return;
        }

        // Use the existing endpoint that returns the full data
        Api.get('reservations/not-reviewed')
            .then(res => {
                // Extract count from the total field
                const total = res.data.total || 0;
                setCount(total);
                setLoading(false);
            })
            .catch(err => {
                console.error('Failed to load new reservations count:', err);
                setLoading(false);
                setCount(0);
            });
    };

    useEffect(() => {
        loadCount();
        
        // Refresh count every 2 minutes
        const interval = setInterval(loadCount, 120000);
        
        return () => clearInterval(interval);
    }, [auth]);

    const refresh = () => {
        loadCount();
    };

    // Allow components to register a callback that runs whenever a live
    // reservation event (created/updated) is received. Returns an
    // unsubscribe function. The callback reference is kept stable so
    // consumers can safely use it as a useEffect dependency.
    const subscribe = useCallback((callback) => {
        subscribersRef.current.add(callback);

        return () => {
            subscribersRef.current.delete(callback);
        };
    }, []);

    // Global socket subscription: keeps the new reservations count in sync
    // regardless of which page is currently mounted, and notifies any
    // subscribed components (see subscribe()).
    useEffect(() => {
        if (!listener || !partnerId) return;

        const channel = listener.private(`partners.${partnerId}`);

        const handleEvent = (e) => {
            loadCount();
            subscribersRef.current.forEach(callback => callback(e));
        };

        channel.listen('ReservationCreated', handleEvent);
        channel.listen('ReservationUpdated', handleEvent);

        return () => {
            channel.stopListening('ReservationCreated');
            channel.stopListening('ReservationUpdated');
        };
    }, [listener, partnerId]);

    return (
        <NewReservationsContext.Provider value={{ count, loading, refresh, setCount, subscribe }}>
            {children}
        </NewReservationsContext.Provider>
    );
}
